// Piezas de Promociones en los lugares del wizard; sin la URL de Promociones no se monta ninguna.
import { useContext, useMemo, useSyncExternalStore } from 'react';
import { QueryClient, QueryClientContext } from '@tanstack/react-query';
import { isPromotionsConfigured } from '@/domains/retail/admin/promotions/api/promotions.client';
import type { AfterReferral } from '@/domains/sales/wizard/api/useReferralFlow';
import type { BuildSaleSlots } from '@/domains/sales/wizard/steps/BuildSaleStep';
import type { PaymentMethodSlots } from '@/domains/sales/wizard/steps/PaymentMethodStep';
import type { PhoneSelectSlots } from '@/domains/sales/wizard/steps/PhoneSelectStep';
import type { QuoteSlots } from '@/domains/sales/wizard/steps/QuoteStep';
import type { ReferralsSlots } from '@/domains/sales/wizard/steps/ReferralsStep';
import type { LineSlots } from '@/domains/sales/wizard/components/LineLabels';
import type { ScoreSlots } from '@/domains/sales/wizard/steps/ScoreStep';
import type { SummarySlots } from '@/domains/sales/wizard/steps/SummaryStep';
import { WIZARD_KEYS } from '@/domains/sales/wizard/api/useWizard';
import { anchorLineOf, extraLinesOf } from '@/domains/sales/wizard/buildSale';
import { sureKeepFinances } from '@/domains/sales/wizard/cardBenefits';
import type { WizardSession } from '@/domains/sales/wizard/types';
import { cachedEvaluation, useStoreSalePromotions } from '../api/useWizardPromotions';
import type { StoreSalePromotionItem } from '../api/wizardPromotionsContract';
import { AccessoryPromotionsSection } from '../components/AccessoryPromotionsSection';
import { AfterSaleBenefits } from '../components/AfterSaleBenefits';
import { DiscountCodeField } from '../components/DiscountCodeField';
import { PromotionNameLabel } from '../components/PromotionNameLabel';
import { ReferralPromotionsBanner } from '../components/ReferralPromotionsBanner';
import { ReferredByNotice } from '../components/ReferredByNotice';
import { SaleClosingBenefits } from '../components/SaleClosingBenefits';
import { SalePromotionsBanner } from '../components/SalePromotionsBanner';
import { PaymentOptionPromotions } from '../components/PaymentOptionPromotions';
import { SalePromotionPicker } from '../components/SalePromotionPicker';
import { SkuPromotionsDetail } from '../components/SkuPromotionsDetail';
import { SummaryGiftName } from '../components/SummaryGiftName';
import { createPromotionsAfterReferral } from '../hooks/promotionsAfterReferral';
import { chosenPromotionIds } from '../lib/promotionSelection';
import { useSessionPromotionState } from '../store/promotionSelectionStore';
import {
    createPromotionsOnSaleChanged,
    seedFromCachedSession,
} from '../hooks/promotionsOnSaleChanged';
import { extraSaleBenefits, NO_EXTRAS_SALE, type ExtrasSaleContext } from '../lib/extrasBenefits';
import {
    forAnchor,
    isSureKeepOnly,
    saleWithoutSureKeep,
    sureKeepOnlyDiscount,
} from '../lib/promotionAppliesTo';
import { rememberedSureKeepOnly } from '../lib/promotionNames';
import type { PaymentSlots } from '@/domains/sales/wizard/steps/PaymentStep';
import { paymentNotEligibleCopy } from '../lib/promotionErrorCopy';
import { cardSaleBenefits } from '../lib/saleBenefits';

export interface PromotionWizardSlots {
    phoneSelect?: PhoneSelectSlots;
    buildSale?: BuildSaleSlots;
    paymentMethod?: PaymentMethodSlots;
    quote?: QuoteSlots;
    /** Cobro: enganche de crédito o pago de contado. */
    payment?: PaymentSlots;
    score?: ScoreSlots;
    summary?: SummarySlots;
    referrals?: ReferralsSlots;
}

export interface PromotionWizardSlotsDeps {
    sessionId: string;
    queryClient?: QueryClient;
    /** Promos de la tienda ya leídas (las pide el banner); de ellas salen burbujas y precios. */
    storePromotions?: readonly StoreSalePromotionItem[];
    /** Promo elegida y extras de la venta: de ellos sale el precio de cada extra. */
    extrasSale?: ExtrasSaleContext;
}

export function buildPromotionWizardSlots({
    sessionId,
    queryClient,
    storePromotions: loadedStorePromotions,
    extrasSale = NO_EXTRAS_SALE,
}: PromotionWizardSlotsDeps): PromotionWizardSlots {
    const storePromotions = loadedStorePromotions ?? [];
    const referral = createPromotionsAfterReferral({ queryClient });
    const afterReferral: AfterReferral = (ctx) => {
        seedFromCachedSession(sessionId, queryClient);
        return referral(ctx);
    };
    const onSaleChanged = createPromotionsOnSaleChanged({ sessionId, queryClient });
    const giftName: NonNullable<LineSlots['giftName']> = (gift) => (
        <SummaryGiftName sessionId={sessionId} sku={gift.sku} catalogName={gift.name} />
    );
    const promotionLabel: NonNullable<LineSlots['promotionLabel']> = (ctx) => (
        <PromotionNameLabel sessionId={sessionId} {...ctx} />
    );
    const lineSlots: LineSlots = { giftName, promotionLabel };
    return {
        phoneSelect: {
            salePromotionsBanner: (items) => (
                <SalePromotionsBanner sessionId={sessionId} items={items} />
            ),
            referredByNotice: (customerName) => (
                <ReferredByNotice sessionId={sessionId} customerName={customerName} />
            ),
            saleBenefits: (item) => cardSaleBenefits(item, storePromotions),
            detailPromotions: (item, _ctx, renderBonuses) => (
                <SkuPromotionsDetail
                    sessionId={sessionId}
                    sku={item.sku}
                    renderBonuses={renderBonuses}
                    storePromotions={loadedStorePromotions}
                    withoutSureKeep={!sureKeepFinances(item)}
                    hideLabel
                />
            ),
        },
        buildSale: {
            referredByNotice: (customerName) => (
                <ReferredByNotice sessionId={sessionId} customerName={customerName} />
            ),
            // El regalo del equipo va dentro de «Promos de venta», sin slot propio.
            salePromotion: (ctx) => <SalePromotionPicker sessionId={sessionId} {...ctx} />,
            discountCode: (ctx) => <DiscountCodeField sessionId={sessionId} {...ctx} />,
            accessoryPromotions: (ctx) => (
                <AccessoryPromotionsSection sessionId={sessionId} context={ctx} />
            ),
            extrasPromotionsBanner: (items) => (
                <SalePromotionsBanner
                    sessionId={sessionId}
                    items={items}
                    screen="extras"
                    anchorWithoutSureKeep={anchorWithoutSureKeep(sessionId, queryClient)}
                />
            ),
            extraBenefits: (item) => {
                const benefits = extraSaleBenefits(
                    item,
                    forAnchor(storePromotions, anchorWithoutSureKeep(sessionId, queryClient)),
                    extrasSale,
                );
                return benefits;
            },
            sureKeepOnlyDiscountCents: (session) =>
                sureKeepOnlyDiscount(session.reservedLines ?? [], {
                    promotion: (id) => evaluatedSureKeepOnly(sessionId, id, queryClient),
                }),
            afterSale: <AfterSaleBenefits sessionId={sessionId} />,
            giftName,
            promotionLabel,
            onSaleChanged,
        },
        paymentMethod: {
            onSaleChanged,
            afterReferral,
            paymentOption: (ctx) => <PaymentOptionPromotions sessionId={sessionId} {...ctx} />,
        },
        quote: lineSlots,
        payment: { ...lineSlots, promotionChargeCopy: paymentNotEligibleCopy },
        score: { afterReferral },
        summary: { coupon: (ctx) => <SaleClosingBenefits {...ctx} />, lines: lineSlots },
        referrals: {
            promotionsBanner: (ctx) => <ReferralPromotionsBanner sessionId={sessionId} {...ctx} />,
        },
    };
}

/** La promo solo aplica con SureKeep, según la evaluación de la venta (o lo visto en la sesión). */
function evaluatedSureKeepOnly(
    sessionId: string,
    promotionId: string,
    queryClient?: QueryClient,
): boolean {
    const evaluation = cachedEvaluation(sessionId, queryClient);
    const promo = evaluation?.promotions.find((p) => p.promotionId === promotionId);
    return promo ? isSureKeepOnly(promo) : rememberedSureKeepOnly(sessionId, promotionId);
}

/** Sin SureKeep para la venta: no financia el equipo o el cliente no califica (según la sesión en caché). */
function anchorWithoutSureKeep(sessionId: string, queryClient?: QueryClient): boolean {
    const session = queryClient?.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
    return session ? saleWithoutSureKeep(session) : false;
}

const noSubscription = (): (() => void) => () => undefined;

/** Sin proveedor de React Query, un cliente propio para que el hook no truene (no pide nada). */
const fallbackClient = new QueryClient({ defaultOptions: { queries: { enabled: false } } });

/** Lugares del wizard con las piezas de Promociones; vacío si Promociones no está configurado. */
export function usePromotionWizardSlots(sessionId: string): PromotionWizardSlots {
    const queryClient = useContext(QueryClientContext);
    // Las pide el slot mismo: el precio de los extras no depende de que el banner las haya leído.
    const storePromotions = useStoreSalePromotions(sessionId, queryClient ?? fallbackClient).data
        ?.items;
    const session = useCachedSession(queryClient, sessionId);
    const chosenIds = chosenPromotionIds(useSessionPromotionState(sessionId).selection).join('|');
    const anchor = session ? anchorLineOf(session) : undefined;
    const extrasSale = useMemo<ExtrasSaleContext>(
        () => ({
            // La que baja los accesorios entre las elegidas.
            chosen:
                storePromotions?.find(
                    (p) =>
                        p.levers.accessoryDiscount && chosenIds.split('|').includes(p.promotionId),
                ) ?? null,
            extraLines: session ? extraLinesOf(session) : [],
            ...(anchor && { anchorLine: anchor }),
        }),
        [storePromotions, chosenIds, session, anchor],
    );
    const configured = isPromotionsConfigured();
    return useMemo(
        () =>
            configured
                ? buildPromotionWizardSlots({
                      sessionId,
                      queryClient,
                      extrasSale,
                      ...(storePromotions && { storePromotions }),
                  })
                : {},
        [configured, sessionId, queryClient, storePromotions, extrasSale],
    );
}

/** La sesión del wizard en caché, sin pedirla. */
function useCachedSession(
    queryClient: QueryClient | undefined,
    sessionId: string,
): WizardSession | undefined {
    const subscribe = useMemo(
        () =>
            queryClient
                ? (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange)
                : noSubscription,
        [queryClient],
    );
    return useSyncExternalStore(subscribe, () =>
        queryClient?.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId)),
    );
}
